/**
 * Server messages into the store. The connection parses and calls these; nothing else changes
 * `confirmed`. A skipped sequence number or a hash that doesn't match asks for a fresh snapshot.
 */
import type {
  EventMsg,
  LeftMsg,
  MemberMsg,
  PresenceRelayMsg,
  RefusedMsg,
  WalkRelayMsg,
  WelcomeMsg,
} from '@bramvasterd/terrella-protocol';
import { applyDiff, fromSnapshot, hashState, type Member } from '@bramvasterd/terrella-rules';

import { logger } from '@core/log';

import { summarizeAway } from './away';
import { seqKey, type Game } from '../game';
import { dropThrough, unacknowledged } from '../intents/pending';
import { rebuildView } from './predict';
import { standOnArrival } from '../player/arrival';
import { createCrewmate, pushSample, startWalk, type Crewmate } from '../player/presence';
import type { Connection } from '../types';

const log = logger('sync');

export function setConnection(game: Game, connection: Connection): void {
  const s = game.store.get();
  if (s.connection === 'outdated' && connection !== 'offline') return;
  if (connection !== 'online')
    game.store.set({ connection, online: new Map(), onlineVersion: s.onlineVersion + 1 });
  else game.store.set({ connection });
}

export function onWelcome(game: Game, msg: WelcomeMsg): void {
  const s = game.store.get();
  if (!s.me || !s.world) return;
  const confirmed = fromSnapshot(msg.snapshot);
  const pending = unacknowledged(s.pending, msg.lastIntentId);
  const online = new Map<string, Crewmate>();
  for (const p of msg.online) {
    if (p.player === s.me.id) continue;
    const mate = createCrewmate(p.player);
    pushSample(mate, p.time, p.p, p.h, p.moving);
    online.set(p.player, mate);
  }
  // the first welcome in a world puts me on its hub; a reconnect leaves me where I was
  if (!s.confirmed) standOnArrival(game.local, confirmed);
  const predicting = msg.rules === game.rulesVersion;
  if (!predicting)
    log.warn(`rules differ: app ${game.rulesVersion}, server ${msg.rules}; not predicting`);
  game.store.set({
    connection: 'online',
    confirmed,
    seq: msg.seq,
    lastIntentId: msg.lastIntentId,
    nextIntentId: Math.max(s.nextIntentId, msg.lastIntentId + 1, ...pending.map((i) => i.id + 1)),
    pending,
    predicting,
    serverRules: msg.rules,
    devTools: msg.devTools,
    members: new Map(msg.members.map((m: Member) => [m.id, m])),
    online,
    onlineVersion: s.onlineVersion + 1,
    // only the first welcome of a session tells what happened while away; reconnects don't
    away: s.confirmed ? s.away : (s.away ?? summarizeAway(msg.since, s.me.id)),
  });
  remember(game, msg.seq);
  rebuildView(game);
  game.events.emit('welcome', { pending });
}

/** The last sequence seen, kept per crew for the next session's hello. */
function remember(game: Game, seq: number): void {
  const world = game.store.get().world;
  if (world) game.deps.storage.set(seqKey(world.id), String(seq)).catch(() => undefined);
}

export function onEvent(game: Game, msg: EventMsg): void {
  const s = game.store.get();
  if (!s.confirmed || !s.me) return;
  if (msg.seq > s.seq + 1) {
    log.warn(`sequence gap: had ${s.seq}, got ${msg.seq}`);
    game.events.emit('resync', undefined);
    return;
  }
  if (msg.seq <= s.seq) {
    // A replay: the server answering a duplicate intent of mine. Clear it; the diff was applied already.
    if (msg.player === s.me.id && msg.intentId !== undefined) {
      game.store.set({ pending: dropThrough(s.pending, msg.intentId) });
      rebuildView(game);
    }
    return;
  }
  const confirmed = s.confirmed;
  if (msg.diff) applyDiff(confirmed, msg.diff);
  if (msg.hash !== undefined && hashState(confirmed) !== msg.hash) {
    log.warn('state hash mismatch');
    game.events.emit('resync', undefined);
    return;
  }
  const mine = msg.player === s.me.id;
  const pending =
    mine && msg.intentId !== undefined ? dropThrough(s.pending, msg.intentId) : s.pending;
  game.store.set({
    confirmed,
    seq: msg.seq,
    pending,
    lastIntentId:
      mine && msg.intentId !== undefined ? Math.max(s.lastIntentId, msg.intentId) : s.lastIntentId,
  });
  remember(game, msg.seq);
  rebuildView(game);
  game.events.emit('confirmed', {
    events: msg.events,
    player: msg.player,
    mine,
    time: msg.time,
  });
}

export function onRefused(game: Game, msg: RefusedMsg): void {
  const s = game.store.get();
  const pending = s.pending.filter((intent) => intent.id !== msg.intentId);
  game.store.set({ pending, lastIntentId: Math.max(s.lastIntentId, msg.intentId) });
  rebuildView(game);
  game.events.emit('refused', { intentId: msg.intentId, refusal: msg.refusal });
}

export function onMember(game: Game, msg: MemberMsg): void {
  const s = game.store.get();
  const members = new Map<string, Member>(s.members);
  if (msg.change === 'removed') members.delete(msg.member.id);
  else members.set(msg.member.id, msg.member);
  if (s.confirmed) {
    if (msg.change === 'removed') s.confirmed.members.delete(msg.member.id);
    else s.confirmed.members.set(msg.member.id, msg.member);
  }
  game.store.set({ members });
  game.events.emit('member', { change: msg.change, member: msg.member });
}

function mate(game: Game, player: string): Crewmate | null {
  const s = game.store.get();
  if (s.me?.id === player) return null;
  const existing = s.online.get(player);
  if (existing) return existing;
  const created = createCrewmate(player);
  const online = new Map(s.online);
  online.set(player, created);
  game.store.set({ online, onlineVersion: s.onlineVersion + 1 });
  game.events.emit('arrived', player);
  return created;
}

export function onPresence(game: Game, msg: PresenceRelayMsg): void {
  const m = mate(game, msg.player);
  if (m) pushSample(m, msg.time, msg.p, msg.h, msg.moving);
}

export function onWalk(game: Game, msg: WalkRelayMsg): void {
  const m = mate(game, msg.player);
  if (m) startWalk(m, msg.to, msg.start, msg.duration);
}

export function onLeft(game: Game, msg: LeftMsg): void {
  const s = game.store.get();
  if (!s.online.has(msg.player)) return;
  const online = new Map(s.online);
  online.delete(msg.player);
  game.store.set({ online, onlineVersion: s.onlineVersion + 1 });
  game.events.emit('left', msg.player);
}

export function onOutdated(game: Game): void {
  game.store.set({ connection: 'outdated' });
}
